import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  getAuth, 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  sendPasswordResetEmail, 
  signOut, 
  updateProfile,
  sendEmailVerification,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  OAuthProvider
} from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { firebaseApp } from '../firebase';
import { getFirestore, doc, setDoc, serverTimestamp } from 'firebase/firestore';

// Get the authentication instance from the main app's firebase instance
const auth = getAuth(firebaseApp);
// Get Firestore instance
const db = getFirestore(firebaseApp);

// Get Functions instance
const functions = getFunctions(firebaseApp /*, 'europe-west1' // Optional: specify region */);

// Prepare callable functions 
const exchangeTokenCallable = httpsCallable(functions, 'exchangeToken');

// Create context
const AuthContext = createContext();

// Custom hook to use auth
export function useAuth() {
  return useContext(AuthContext);
}

// Redirect settings after email verification
const actionCodeSettings = {
  // IMPORTANT: This URL MUST be authorized in your Firebase console
  // Authentication -> Settings -> Authorized domains
  // It also needs to handle the verification logic (Firebase does this automatically
  // if the domain matches the project's default auth domain or is added)
  url: `https://app.protegosafe.eu/login?verified=true`, // Explicitly set the app domain
  handleCodeInApp: false, // Set to false for web redirects
};

// Helper function to create a user record in Firestore
async function createUserRecord(user) {
  if (!user) return;
  
  try {
    // Calculate trial end date (14 days from now)
    const trialEndDate = new Date();
    trialEndDate.setDate(trialEndDate.getDate() + 14);
    
    const userRef = doc(db, 'users', user.uid);
    await setDoc(userRef, {
      displayName: user.displayName || '',
      email: user.email,
      emailVerified: user.emailVerified,
      createdAt: serverTimestamp(),
      isAdmin: false,
      searchCadence: '1year',
      trialInfo: {
        isInTrial: true,
        trialStartDate: serverTimestamp(),
        trialEndDate: trialEndDate
      }
    }, { merge: true });
    
    console.log('User record created in Firestore');
  } catch (error) {
    console.error('Error creating user record:', error);
  }
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Function to get Custom Token from backend
  async function getCustomToken(idToken) {
    try {
      console.log('AuthContext: Calling exchangeTokenCallable with idToken:', idToken ? `Token length: ${idToken.length}` : 'Token is null/undefined');
      console.log('AuthContext: Type of idToken:', typeof idToken);
      if (idToken && typeof idToken === 'string') {
        console.log('AuthContext: Token preview:', `${idToken.substring(0, 10)}...${idToken.substring(idToken.length - 10)}`);
      }

      console.log('Requesting custom token from backend...');
      const result = await exchangeTokenCallable({ idToken });
      console.log('Received custom token successfully.');
      return result.data.customToken;
    } catch (error) {
      console.error('Error calling exchangeToken function:', error);
      // Handle specific errors if needed (e.g., based on error.code or error.message)
      setError('Failed to prepare authentication session. Please try again.'); 
      throw error; // Re-throw for calling function to handle
    }
  }

  // Function to redirect to the main application
  function redirectToApp(customToken) {
    if (customToken) {
      console.log('AuthContext: Redirecting to app with custom token...');
      window.location.href = `https://app.protegosafe.eu/auth?customToken=${encodeURIComponent(customToken)}`;
    } else {
      console.error('AuthContext: Custom token missing, cannot redirect properly.');
      // Fallback redirect without token (user will need to log in manually)
      window.location.href = "https://app.protegosafe.eu/login"; 
    }
  }

  // Function to sign up a new user
  async function signup(email, password, displayName) {
    try {
      setError('');
      
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      await updateProfile(userCredential.user, { displayName });
      
      // Create a record in Firestore
      await createUserRecord({
        ...userCredential.user,
        displayName // Ensure displayName is included since it was just updated
      });
      
      // Send email verification
      await sendEmailVerification(userCredential.user, actionCodeSettings);
      console.log(`Verification email sent to ${email} with redirect URL: ${actionCodeSettings.url}`);
      
      // Get ID token and then exchange for custom token
      const idToken = await userCredential.user.getIdToken(true);
      const customToken = await getCustomToken(idToken);
      
      return { user: userCredential.user, customToken }; // Return both
    } catch (err) {
      console.error('Signup or token exchange error:', err);
      
      // Format the error message to be more user-friendly
      const errorMessage = getFormattedErrorMessage(err);
      setError(errorMessage);
      
      // Preserve the original error object to allow error code checking in components
      throw err;
    }
  }

  // Helper function to format error messages
  function getFormattedErrorMessage(err) {
    switch(err.code) {
      case 'auth/email-already-in-use':
        return 'This email address is already associated with an account.';
      case 'auth/invalid-email':
        return 'Please enter a valid email address.';
      case 'auth/weak-password':
        return 'Your password is too weak. Please create a stronger password.';
      case 'auth/network-request-failed':
        return 'A network error occurred. Please check your internet connection and try again.';
      case 'auth/user-not-found':
        return 'No account found with this email address.';
      case 'auth/wrong-password':
        return 'Incorrect password. Please try again or reset your password.';
      case 'auth/too-many-requests':
        return 'Too many unsuccessful login attempts. Please try again later or reset your password.';
      case 'auth/popup-blocked':
        return 'Sign-in popup was blocked by your browser. Please allow popups for this site.';
      case 'auth/popup-closed-by-user':
        return 'Sign-in was cancelled. Please try again.';
      case 'auth/account-exists-with-different-credential':
        return 'An account with this email already exists using a different sign-in method.';
      case 'auth/invalid-credential':
        return 'Your login credentials are invalid. Please try again.';
      case 'auth/operation-not-allowed':
        return 'This sign-in method is not enabled. Please contact support.';
      case 'auth/requires-recent-login':
        return 'This operation requires a more recent login. Please sign in again.';
      case 'auth/user-disabled':
        return 'This account has been disabled. Please contact support.';
      default:
        return err.message || 'An error occurred. Please try again later.';
    }
  }

  // Function to login a user
  async function login(email, password) {
    try {
      setError('');
      const result = await signInWithEmailAndPassword(auth, email, password);
      
      // Get ID token and then exchange for custom token
      const idToken = await result.user.getIdToken(true);
      const customToken = await getCustomToken(idToken);

      return { user: result.user, customToken }; // Return both
    } catch (err) {
      console.error('Login or token exchange error:', err);
      const errorMessage = getFormattedErrorMessage(err);
      setError(errorMessage);
      throw err;
    }
  }

  // Function for password reset
  async function resetPassword(email) {
    try {
      setError('');
      await sendPasswordResetEmail(auth, email);
    } catch (err) {
      console.error('Password reset error:', err);
      const errorMessage = getFormattedErrorMessage(err);
      setError(errorMessage);
      throw err;
    }
  }

  // Function to logout
  async function logout() {
    try {
      setError('');
      await signOut(auth);
    } catch (err) {
      console.error('Logout error:', err);
      setError(err.message);
      throw err;
    }
  }

  // Google sign-in with popup
  async function googleSignInWithPopup() {
    try {
      setError('');
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      
      // Create or update user record in Firestore
      await createUserRecord(result.user);
      
      // Get ID token and then exchange for custom token
      const idToken = await result.user.getIdToken(true);
      const customToken = await getCustomToken(idToken);

      return { user: result.user, customToken }; // Return both
    } catch (err) {
      console.error('Google sign-in popup error:', err);
      const errorMessage = getFormattedErrorMessage(err);
      setError(errorMessage);
      throw err;
    }
  }

  // Google sign-in with redirect
  async function googleSignIn() {
    try {
      setError('');
      const provider = new GoogleAuthProvider();
      await signInWithRedirect(auth, provider);
    } catch (err) {
      console.error('Google sign-in redirect error:', err);
      const errorMessage = getFormattedErrorMessage(err);
      setError(errorMessage);
      throw err;
    }
  }

  // Microsoft sign-in with popup
  async function microsoftSignInWithPopup() {
    try {
      setError('');
      const provider = new OAuthProvider('microsoft.com');
      const result = await signInWithPopup(auth, provider);
      
      // Create or update user record in Firestore
      await createUserRecord(result.user);
      
      // Get ID token and then exchange for custom token
      const idToken = await result.user.getIdToken(true);
      const customToken = await getCustomToken(idToken);

      return { user: result.user, customToken }; // Return both
    } catch (err) {
      console.error('Microsoft sign-in popup error:', err);
      const errorMessage = getFormattedErrorMessage(err);
      setError(errorMessage);
      throw err;
    }
  }

  // Microsoft sign-in with redirect
  async function microsoftSignIn() {
    try {
      setError('');
      const provider = new OAuthProvider('microsoft.com');
      await signInWithRedirect(auth, provider);
    } catch (err) {
      console.error('Microsoft sign-in redirect error:', err);
      const errorMessage = getFormattedErrorMessage(err);
      setError(errorMessage);
      throw err;
    }
  }

  // Get result from redirect operation
  async function getRedirectSignInResult() {
    try {
      const result = await getRedirectResult(auth);
      if (result) {
        // Create or update user record in Firestore for redirect auth
        await createUserRecord(result.user);
        
        // After creating the record, get the user object to proceed
        const user = result.user;
        console.log('AuthContext: User authenticated via redirect:', user.uid);
        
        // Get ID token and custom token
        const idToken = await user.getIdToken(true);
        const customToken = await getCustomToken(idToken);
        
        // Redirect to the app with the custom token
        redirectToApp(customToken);
        
        // It's useful to return the user, although the page will redirect
        return user;
      }
      return null;
    } catch (err) {
      console.error('Redirect result error:', err);
      setError(err.message);
      throw err;
    }
  }

  // Set up auth state listener
  useEffect(() => {
    // This listener solely relies on Firebase to report the auth state change
    // for this specific origin (www.protegosafe.eu)
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('Website Auth Listener - User:', user ? user.uid : 'null');
      setCurrentUser(user);
      setLoading(false);
    });

    // Check for redirect result (for OAuth flows initiated on the website)
    // This needs to be async to handle token exchange and redirection
    const handleRedirect = async () => {
      try {
        // The getRedirectSignInResult will now handle the full flow
        await getRedirectSignInResult();
      } catch (error) {
        // Error is already logged in getRedirectSignInResult, but we can log here too
        console.error('AuthContext: Failed to process redirect sign-in.', error);
        // Optionally set a user-facing error message
        setError(getFormattedErrorMessage(error));
      }
    };
    
    handleRedirect();

    return () => unsubscribe();
  }, []);

  const value = {
    currentUser,
    signup,
    login,
    logout,
    resetPassword,
    error,
    setError,
    googleSignIn,
    googleSignInWithPopup,
    microsoftSignIn,
    microsoftSignInWithPopup,
    getCustomToken,
    getFormattedErrorMessage,
    redirectToApp
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
} 